import { apiService } from './api';

export interface ImportResult {
  success: boolean;
  imported: number;
  errors: string[];
  warnings: string[];
}

export interface ExportOptions {
  format: 'csv' | 'excel';
  status?: string[];
  includeAssignments?: boolean;
  includeInfluence?: boolean;
  includeWiki?: boolean;
  includeMedia?: boolean;
}

export const adminService = {
  /**
   * Export stakeholders
   */
  async exportStakeholders(options: ExportOptions): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', options.format);
    if (options.status && options.status.length > 0) {
      params.append('status', options.status.join(','));
    }
    if (options.includeAssignments) {
      params.append('includeAssignments', 'true');
    }
    if (options.includeInfluence) {
      params.append('includeInfluence', 'true');
    }
    if (options.includeWiki) {
      params.append('includeWiki', 'true');
    }
    if (options.includeMedia) {
      params.append('includeMedia', 'true');
    }

    const response = await apiService.get(`/api/admin/export/stakeholders?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Export organizations
   */
  async exportOrganizations(options: ExportOptions): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', options.format);
    if (options.status && options.status.length > 0) {
      params.append('status', options.status.join(','));
    }
    if (options.includeAssignments) {
      params.append('includeAssignments', 'true');
    }
    if (options.includeInfluence) {
      params.append('includeInfluence', 'true');
    }
    if (options.includeWiki) {
      params.append('includeWiki', 'true');
    }
    if (options.includeMedia) {
      params.append('includeMedia', 'true');
    }

    const response = await apiService.get(`/api/admin/export/organizations?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Import stakeholders
   */
  async importStakeholders(file: File, format: 'csv' | 'excel' = 'excel'): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('format', format);

    const response = await apiService.post('/api/admin/import/stakeholders', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Download stakeholder template
   */
  async downloadStakeholderTemplate(format: 'csv' | 'excel' = 'excel'): Promise<Blob> {
    const response = await apiService.get(`/api/admin/template/stakeholders?format=${format}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Download organization template
   */
  async downloadOrganizationTemplate(format: 'csv' | 'excel' = 'excel'): Promise<Blob> {
    const response = await apiService.get(`/api/admin/template/organizations?format=${format}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Import organizations
   */
  async importOrganizations(file: File, format: 'csv' | 'excel' = 'excel'): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('format', format);

    const response = await apiService.post('/api/admin/import/organizations', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Bulk export
   */
  async bulkExport(entities: ('stakeholders' | 'organizations')[], format: 'csv' | 'excel' = 'excel'): Promise<Blob> {
    const response = await apiService.post('/api/admin/bulk/export', {
      entities,
      format,
    }, {
      responseType: 'blob',
    });
    return response.data;
  },
};
